import { useState } from "react";
import { Play, Quote, Heart } from "lucide-react";
import { Button } from "../ui/button";

// TODO: Replace with the founder's actual video once recorded
// Supports YouTube (embed URL), Vimeo, or self-hosted MP4
const FOUNDER_VIDEO = {
  // Set to the founder's 60-90 second welcome video URL
  // Example YouTube: "https://www.youtube.com/embed/XXXXXXXXXXX"
  // Example MP4: "https://.../founder.mp4"
  url: "", // leave "" to show the elegant placeholder
  name: "Aarohi Welfare Foundation",
  title: "Founders • Aadi Gayanam Play School",
  monogram: "आ",
  message:
    "We started Aadi Gayanam because we dreamt of a school where our own child would love to go — a place rooted in Bharatiya values, filled with love and imagination, with teachers who feel like family. This is our small offering to Jaipur and to Bharat's next generation."
};

const FounderMessage = () => {
  const [playing, setPlaying] = useState(false);
  const v = FOUNDER_VIDEO;

  return (
    <section className="py-16 bg-gradient-to-br from-orange-950 via-red-950 to-amber-950 relative overflow-hidden" data-testid="founder-message">
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <div className="absolute -top-16 -left-16 w-80 h-80 border-2 border-yellow-300 rounded-full" />
        <div className="absolute top-1/3 -right-16 w-96 h-96 border-2 border-orange-300 rounded-full" />
        <div className="absolute bottom-0 left-1/4 w-56 h-56 border border-yellow-300 rounded-full" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-5 gap-10 items-center">
          {/* Video / Placeholder — 3 columns */}
          <div className="lg:col-span-3">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl border-4 border-yellow-200/20 aspect-video bg-gradient-to-br from-orange-900 to-red-900">
              {v.url && playing ? (
                v.url.includes("youtube.com") || v.url.includes("vimeo.com") ? (
                  <iframe
                    src={`${v.url}${v.url.includes("?") ? "&" : "?"}autoplay=1`}
                    title="Founder's Message"
                    className="w-full h-full"
                    frameBorder="0"
                    allow="autoplay; encrypted-media; picture-in-picture"
                    allowFullScreen
                    data-testid="founder-video-iframe"
                  />
                ) : (
                  <video src={v.url} controls autoPlay className="w-full h-full object-cover" data-testid="founder-video-mp4" />
                )
              ) : (
                <button
                  onClick={() => v.url && setPlaying(true)}
                  className={`w-full h-full flex items-center justify-center relative ${v.url ? "cursor-pointer group" : "cursor-default"}`}
                  data-testid="founder-play-btn"
                  disabled={!v.url}
                >
                  {/* Decorative pattern */}
                  <div className="absolute inset-0 opacity-30 pointer-events-none">
                    <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
                      <defs>
                        <pattern id="founder-dots" x="0" y="0" width="32" height="32" patternUnits="userSpaceOnUse">
                          <circle cx="6" cy="6" r="1.5" fill="rgba(255,220,120,0.4)" />
                        </pattern>
                      </defs>
                      <rect width="100%" height="100%" fill="url(#founder-dots)" />
                    </svg>
                  </div>
                  {/* Monogram medallion */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-40 h-40 rounded-full bg-gradient-to-br from-yellow-300 to-orange-400 flex items-center justify-center shadow-2xl border-4 border-white/40">
                      <span className="font-devanagari text-7xl font-bold text-orange-900">{v.monogram}</span>
                    </div>
                  </div>
                  {/* Play button overlay */}
                  {v.url && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-20 h-20 rounded-full bg-white/95 flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform">
                        <Play className="w-8 h-8 text-orange-700 fill-orange-700 ml-1" />
                      </div>
                    </div>
                  )}
                  {/* Bottom info bar */}
                  <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/70 to-transparent text-left">
                    <p className="text-[10px] font-bold text-yellow-200 uppercase tracking-widest mb-0.5">
                      {v.url ? "Founder's Message" : "Founder's Message • Coming Soon"}
                    </p>
                    <p className="text-white font-display font-bold text-lg">{v.name}</p>
                  </div>
                </button>
              )}
            </div>
          </div>

          {/* Message text — 2 columns */}
          <div className="lg:col-span-2 text-white" data-testid="founder-quote">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-yellow-400/20 border border-yellow-300/30 mb-4">
              <Heart className="w-3.5 h-3.5 text-yellow-200 fill-yellow-200" />
              <span className="text-[10px] font-bold text-yellow-100 uppercase tracking-widest">From Our Hearts to Yours</span>
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-bold mb-4 leading-tight">
              A Note from the <span className="text-yellow-200">Founders</span>
            </h2>
            <Quote className="w-8 h-8 text-yellow-300/60 mb-2" />
            <p className="text-orange-50 text-base leading-relaxed italic mb-5">
              "{v.message}"
            </p>
            <div className="flex items-center gap-3 pt-4 border-t border-white/20">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-yellow-300 to-orange-400 flex items-center justify-center shadow">
                <span className="font-devanagari text-xl font-bold text-orange-900">{v.monogram}</span>
              </div>
              <div>
                <p className="font-display font-bold text-yellow-100">{v.name}</p>
                <p className="text-[11px] text-orange-200">{v.title}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FounderMessage;
